"use client"

import { useMemo, useState, type ReactNode } from "react"
import { BookOpen, Gem, Info, Settings2, HelpCircle, Save, Link2 } from "lucide-react"
import type { PersistedGameState } from "@/services/game-state-persistence"
import { useCrystals } from "@/hooks/use-crystals"
import { usePlayerPrefs } from "@/lib/player-prefs"
import { usePlayerStats } from "@/lib/player-stats"
import { heroTypes } from "@/data/hero-types"
import { ChartIcon, TrophyIcon, UsersIcon } from "@/components/icons/pixel-icons"
import { MenuShell } from "./menu-shell"
import { MenuLayoutContext, useIsNarrowViewport } from "./menu-layout-context"
import { MenuGroup, MenuNavRow } from "./menu-primitives"
import { EncyclopelfiaSection } from "./sections/encyclopelfia-section"
import { CrystalsSection } from "./sections/crystals-section"
import {
  GlobalRecordsSection,
  LocalRecordsSection,
  OnChainRecordsSection,
} from "./sections/records-section"
import { HeroesSection } from "./sections/heroes-section"
import {
  AboutSection,
  AudioDisplaySection,
  HowToPlaySection,
  RunSaveSection,
} from "./sections/settings-sections"

type EntryId =
  | "encyclopelfia"
  | "crystals"
  | "records-local"
  | "records-global"
  | "records-onchain"
  | "heroes"
  | "audio"
  | "how-to-play"
  | "run-save"
  | "about"

interface MenuEntry {
  id: EntryId
  label: string
  sublabel: string
  icon: ReactNode
  /** Right-aligned summary on the nav row */
  value?: ReactNode
  render: () => ReactNode
}

interface MenuGroupDef {
  title: string
  icon: ReactNode
  entries: MenuEntry[]
}

export interface GameMenuProps {
  isOpen: boolean
  onClose: () => void
  isMobile?: boolean
  /** Opens the existing tutorial modal from the How to play section */
  onOpenTutorial?: () => void
  /** Saved-run controls, only meaningful on the title screen */
  pendingRun?: PersistedGameState | null
  onResumeRun?: () => void
  onDiscardRun?: () => void
}

/**
 * The game's main menu — one surface behind the hamburger button on both the
 * title screen and the in-game HUD.
 *
 * Entries live in a single registry so the desktop two-pane layout and the
 * mobile drill-down render exactly the same set; adding a section means adding
 * one object, not touching two layouts.
 */
export const GameMenu = ({
  isOpen,
  onClose,
  isMobile = false,
  onOpenTutorial,
  pendingRun = null,
  onResumeRun,
  onDiscardRun,
}: GameMenuProps) => {
  const isNarrow = useIsNarrowViewport(isMobile)
  const [activeId, setActiveId] = useState<EntryId | null>(null)

  const { balance, isConnected } = useCrystals()
  const stats = usePlayerStats()
  const prefs = usePlayerPrefs()

  // Derived rather than synced: the desktop detail pane always shows something,
  // while the mobile drill-down starts on the list with nothing selected.
  const effectiveId: EntryId | null = isNarrow ? activeId : (activeId ?? "encyclopelfia")

  const heroLabel =
    prefs.preferredHero === "random"
      ? "Random"
      : (heroTypes[prefs.preferredHero]?.name ?? prefs.preferredHero)

  const groups: MenuGroupDef[] = useMemo(
    () => [
      {
        title: "Library",
        icon: <BookOpen size={14} />,
        entries: [
          {
            id: "encyclopelfia",
            label: "Encyclopelfia",
            sublabel: "Towers, enemies and items",
            icon: <BookOpen size={18} />,
            render: () => <EncyclopelfiaSection isNarrow={isNarrow} />,
          },
        ],
      },
      {
        title: "Crystals",
        icon: <Gem size={14} />,
        entries: [
          {
            id: "crystals",
            label: "Manage crystals",
            sublabel: "Buy crystals and claim ladder earnings",
            icon: <Gem size={18} />,
            value: isConnected ? balance.toLocaleString() : undefined,
            render: () => <CrystalsSection />,
          },
        ],
      },
      {
        title: "Records",
        icon: <TrophyIcon size={14} />,
        entries: [
          {
            id: "records-local",
            label: "This device",
            sublabel: "Your runs, best scores and per-hero history",
            icon: <ChartIcon size={18} />,
            value: stats.bestScore > 0 ? stats.bestScore.toLocaleString() : undefined,
            render: () => <LocalRecordsSection />,
          },
          {
            id: "records-global",
            label: "Global ladder",
            sublabel: "Best score per wallet across all players",
            icon: <TrophyIcon size={18} />,
            render: () => <GlobalRecordsSection />,
          },
          {
            id: "records-onchain",
            label: "On-chain records",
            sublabel: "Publish a personal best to the blockchain",
            icon: <Link2 size={18} />,
            render: () => <OnChainRecordsSection />,
          },
        ],
      },
      {
        title: "Gameplay",
        icon: <Settings2 size={14} />,
        entries: [
          {
            id: "heroes",
            label: "Heroes",
            sublabel: "Which hero you start a run with",
            icon: <UsersIcon size={18} />,
            value: heroLabel,
            render: () => <HeroesSection />,
          },
          {
            id: "audio",
            label: "Audio & display",
            sublabel: "Volumes, default speed, motion",
            icon: <Settings2 size={18} />,
            render: () => <AudioDisplaySection />,
          },
          {
            id: "how-to-play",
            label: "How to play",
            sublabel: "Tutorial and the basics",
            icon: <HelpCircle size={18} />,
            render: () => (
              <HowToPlaySection
                onOpenTutorial={() => {
                  onClose()
                  onOpenTutorial?.()
                }}
              />
            ),
          },
        ],
      },
      {
        title: "Data",
        icon: <Save size={14} />,
        entries: [
          {
            id: "run-save",
            label: "Run & save data",
            sublabel: "Saved run and local records",
            icon: <Save size={18} />,
            render: () => (
              <RunSaveSection
                pendingRun={pendingRun}
                onResumeRun={
                  onResumeRun
                    ? () => {
                        onClose()
                        onResumeRun()
                      }
                    : undefined
                }
                onDiscardRun={onDiscardRun}
              />
            ),
          },
          {
            id: "about",
            label: "About",
            sublabel: "Connection, credits and version",
            icon: <Info size={18} />,
            render: () => <AboutSection />,
          },
        ],
      },
    ],
    [
      isNarrow,
      isConnected,
      balance,
      stats.bestScore,
      heroLabel,
      onClose,
      onOpenTutorial,
      pendingRun,
      onResumeRun,
      onDiscardRun,
    ],
  )

  const activeEntry = useMemo(
    () => groups.flatMap((g) => g.entries).find((e) => e.id === effectiveId) ?? null,
    [groups, effectiveId],
  )

  const navContent = (
    <nav aria-label="Menu sections">
      {groups.map((group) => (
        <MenuGroup key={group.title} title={group.title} icon={group.icon}>
          {group.entries.map((entry) => (
            <MenuNavRow
              key={entry.id}
              icon={entry.icon}
              label={entry.label}
              sublabel={entry.sublabel}
              value={entry.value}
              isActive={!isNarrow && entry.id === effectiveId}
              onClick={() => setActiveId(entry.id)}
            />
          ))}
        </MenuGroup>
      ))}
    </nav>
  )

  return (
    <MenuLayoutContext.Provider value={{ isNarrow }}>
      <MenuShell
        isOpen={isOpen}
        onClose={onClose}
        title="Menu"
        navContent={navContent}
        detailContent={activeEntry ? activeEntry.render() : null}
        detailTitle={activeEntry?.label}
        onBack={() => setActiveId(null)}
        isNarrow={isNarrow}
      />
    </MenuLayoutContext.Provider>
  )
}
